/**
 * RetryManager - Intelligent retry handling for failed operations
 * 
 * Provides exponential backoff retry logic with legal document
 * integrity protection and permanent failure handling.
 */

import { Operation } from '../types/DocumentTypes';

interface RetryableOperation {
  operation: Operation;
  originalError: string;
  retryCount: number;
  nextRetryTime: number;
  maxRetries: number;
  backoffMultiplier: number;
}

interface RetryStrategy {
  maxRetries: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitterFactor: number;
}

export class RetryManager {
  private retryQueue: RetryableOperation[] = [];
  private retryTimer: NodeJS.Timeout | null = null;
  private defaultStrategy: RetryStrategy;
  
  // Retry statistics
  private totalRetries: number = 0;
  private successfulRetries: number = 0;
  private permanentFailures: number = 0;

  constructor() {
    this.defaultStrategy = {
      maxRetries: 5,
      initialDelay: 1000,      // 1 second
      maxDelay: 30000,         // 30 seconds
      backoffMultiplier: 2,
      jitterFactor: 0.1        // 10% random jitter
    };
    
    this.startRetryLoop();
  }

  // ============================================================================
  // RETRY QUEUING
  // ============================================================================

  /**
   * Queue operations for retry
   */
  queueForRetry(operations: Operation[], error: string): void {
    const now = Date.now();
    
    for (const operation of operations) {
      const strategy = this.getRetryStrategy(operation);
      
      const retryableOp: RetryableOperation = {
        operation,
        originalError: error,
        retryCount: 0,
        nextRetryTime: now + strategy.initialDelay,
        maxRetries: strategy.maxRetries,
        backoffMultiplier: strategy.backoffMultiplier
      };
      
      this.retryQueue.push(retryableOp);
    }
    
    console.log(`[RetryManager] Queued ${operations.length} operations for retry`);
  }

  /**
   * Get retry strategy based on operation type and context
   */
  private getRetryStrategy(operation: Operation): RetryStrategy {
    // Critical operations get more aggressive retry
    if (this.isCriticalOperation(operation)) {
      return {
        ...this.defaultStrategy,
        maxRetries: 10,
        initialDelay: 500,
        maxDelay: 60000
      };
    }
    
    // Legal document operations get standard retry
    if (this.isLegalOperation(operation)) {
      return {
        ...this.defaultStrategy,
        maxRetries: 7
      };
    }
    
    // Formatting operations get limited retry
    if (this.isFormattingOperation(operation)) {
      return {
        ...this.defaultStrategy,
        maxRetries: 3,
        maxDelay: 10000
      };
    }
    
    return this.defaultStrategy;
  }

  // ============================================================================
  // RETRY EXECUTION
  // ============================================================================

  /**
   * Start retry processing loop
   */
  private startRetryLoop(): void {
    this.retryTimer = setInterval(() => {
      this.processRetries();
    }, 1000); // Check every second
  }

  /**
   * Process pending retries
   */
  private async processRetries(): Promise<void> {
    const now = Date.now();
    const readyForRetry = this.retryQueue.filter(retryOp => 
      retryOp.nextRetryTime <= now && retryOp.retryCount < retryOp.maxRetries
    );
    
    if (readyForRetry.length === 0) return;
    
    console.log(`[RetryManager] Processing ${readyForRetry.length} retry operations`);
    
    for (const retryOp of readyForRetry) {
      await this.executeRetry(retryOp);
    }
    
    // Clean up completed retries
    this.cleanupCompletedRetries();
  }

  /**
   * Execute single retry operation
   */
  private async executeRetry(retryOp: RetryableOperation): Promise<void> {
    this.totalRetries++;
    retryOp.retryCount++;
    
    try {
      console.log(`[RetryManager] Retrying ${retryOp.operation.type} (attempt ${retryOp.retryCount}/${retryOp.maxRetries})`);
      
      // Attempt to sync operation
      const success = await this.attemptSync(retryOp.operation);
      
      if (success) {
        // Retry successful
        this.successfulRetries++;
        retryOp.nextRetryTime = Date.now() + 86400000; // Mark as completed (24 hours in future)
        console.log(`[RetryManager] Retry successful for ${retryOp.operation.type}`);
      } else {
        // Retry failed, schedule next attempt
        this.scheduleNextRetry(retryOp);
      }
      
    } catch (error) {
      console.error(`[RetryManager] Retry attempt failed:`, error);
      this.scheduleNextRetry(retryOp);
    }
  }

  /**
   * Schedule next retry with exponential backoff
   */
  private scheduleNextRetry(retryOp: RetryableOperation): void {
    if (retryOp.retryCount >= retryOp.maxRetries) {
      // Permanent failure
      this.handlePermanentFailure(retryOp);
      return;
    }
    
    // Calculate next retry time with exponential backoff
    const baseDelay = this.defaultStrategy.initialDelay * 
                     Math.pow(retryOp.backoffMultiplier, retryOp.retryCount);
    
    // Add jitter to prevent thundering herd
    const jitter = baseDelay * this.defaultStrategy.jitterFactor * (Math.random() - 0.5);
    const delay = Math.min(baseDelay + jitter, this.defaultStrategy.maxDelay);
    
    retryOp.nextRetryTime = Date.now() + delay;
    
    console.log(`[RetryManager] Scheduled next retry in ${(delay / 1000).toFixed(1)}s (attempt ${retryOp.retryCount + 1}/${retryOp.maxRetries})`);
  }

  /**
   * Handle operations that have permanently failed
   */
  private handlePermanentFailure(retryOp: RetryableOperation): void {
    this.permanentFailures++;
    
    console.error(`[RetryManager] Permanent failure for ${retryOp.operation.type} operation:`, retryOp.originalError);
    
    // Emit event for UI notification
    const event = new CustomEvent('edgedocs-permanent-failure', {
      detail: {
        operation: retryOp.operation,
        error: retryOp.originalError,
        retryCount: retryOp.retryCount
      }
    });
    window.dispatchEvent(event);
    
    // Log for legal audit trail
    this.logPermanentFailure(retryOp);
  }

  private logPermanentFailure(retryOp: RetryableOperation): void {
    const auditEntry = {
      timestamp: new Date(),
      operationId: retryOp.operation.id,
      operationType: retryOp.operation.type,
      attorney: retryOp.operation.attorney.name,
      error: retryOp.originalError,
      retryCount: retryOp.retryCount,
      status: 'permanent_failure'
    };
    
    console.log('[RetryManager] Audit log:', auditEntry);
    // Will be sent to legal audit system
  }

  // ============================================================================
  // SYNC ATTEMPT
  // ============================================================================

  private async attemptSync(operation: Operation): Promise<boolean> {
    // Placeholder for actual sync implementation
    // Will be integrated with SyncManager
    
    try {
      // Simulate sync attempt
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Random success/failure for testing
      return Math.random() > 0.2; // 80% success rate
      
    } catch (error) {
      return false;
    }
  }

  // ============================================================================
  // OPERATION CLASSIFICATION
  // ============================================================================

  private isCriticalOperation(operation: Operation): boolean {
    // Critical operations that must not be lost
    return (
      this.isSeniorAttorneyOperation(operation) ||
      this.isLargeOperation(operation) ||
      this.affectsLegalContent(operation)
    );
  }

  private isLegalOperation(operation: Operation): boolean {
    // Operations affecting legal document structure
    return (
      operation.type === 'insert' || 
      operation.type === 'delete'
    );
  }

  private isFormattingOperation(operation: Operation): boolean {
    return operation.type === 'format';
  }

  private isSeniorAttorneyOperation(operation: Operation): boolean {
    return operation.attorney.role === 'partner' || operation.attorney.seniority > 5;
  }

  private isLargeOperation(operation: Operation): boolean {
    if (operation.type === 'insert') {
      return (operation as any).content?.length > 50;
    }
    if (operation.type === 'delete') {
      const deleteOp = operation as any;
      return (deleteOp.end - deleteOp.start) > 50;
    }
    return false;
  }

  private affectsLegalContent(operation: Operation): boolean {
    // Placeholder for legal content detection
    // Will be enhanced with actual legal text analysis
    return false;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  private cleanupCompletedRetries(): void {
    const now = Date.now();
    const beforeCount = this.retryQueue.length;
    
    // Remove operations that are completed or too old
    this.retryQueue = this.retryQueue.filter(retryOp => {
      const isCompleted = retryOp.nextRetryTime > now + 3600000; // Completed (marked far in future)
      const isTooOld = (now - retryOp.operation.timestamp.getTime()) > 86400000; // Older than 24 hours
      const isPermanentFailure = retryOp.retryCount >= retryOp.maxRetries;
      
      return !(isCompleted || isTooOld || isPermanentFailure);
    });
    
    const removedCount = beforeCount - this.retryQueue.length;
    if (removedCount > 0) {
      console.log(`[RetryManager] Cleaned up ${removedCount} completed retry operations`);
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get retry statistics
   */
  getRetryStatistics(): {
    totalRetries: number;
    successfulRetries: number;
    permanentFailures: number;
    successRate: number;
    pendingRetries: number;
  } {
    const successRate = this.totalRetries > 0 ? this.successfulRetries / this.totalRetries : 1;
    
    return {
      totalRetries: this.totalRetries,
      successfulRetries: this.successfulRetries,
      permanentFailures: this.permanentFailures,
      successRate,
      pendingRetries: this.retryQueue.length
    };
  }

  /**
   * Force retry all failed operations
   */
  forceRetryAll(): void {
    const now = Date.now();
    
    for (const retryOp of this.retryQueue) {
      if (retryOp.retryCount < retryOp.maxRetries) {
        retryOp.nextRetryTime = now; // Retry immediately
      }
    }
    
    console.log(`[RetryManager] Forced immediate retry for ${this.retryQueue.length} operations`);
  }

  /**
   * Clear all retry operations
   */
  clear(): void {
    this.retryQueue = [];
    console.log('[RetryManager] Retry queue cleared');
  }

  /**
   * Stop retry processing
   */
  destroy(): void {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    
    this.clear();
    console.log('[RetryManager] Destroyed');
  }
}
